import React from 'react'
import PropTypes from 'prop-types'
import {textSize} from '../helpers/math'
import ReactTable from 'react-table'
import _ from 'lodash'

const cfxProReactTable = (ComponentToWrap) => {
  return class Wrapper extends React.Component {
    constructor(props) {
      super(props)

      this._tBodyComponent = null
      this._tBodyGroups = null

      this.state = {height: 0, width: 0}
    }

    isIE = () => {
      var ua = window.navigator.userAgent
      var is_ie = /MSIE|Trident/.test(ua)

      return is_ie
    }

    isIOS9OrBelow = () => {
      var ua = window.navigator.userAgent
      var is_ios = /iPad|iPhone|iPod/.test(ua)

      const version = parseInt(navigator.appVersion, 10)

      return is_ios && version < 10
    }

    handleScroll = (event) => {
      let headers = document.getElementsByClassName('rt-thead')
      for (let i = 0; i < headers.length; i++) {
        headers[i].scrollLeft = event.target.scrollLeft
      }
    }

    componentDidMount() {
      this.updateWindowDimensions()
      if (this.props.fixedHeaders) {
        window.addEventListener('resize', () => this.updateWindowDimensions())
      }
    }

    componentWillUnmount() {
      if (this.props.fixedHeaders) {
        window.removeEventListener('resize', () =>
          this.updateWindowDimensions(),
        )
      }
    }

    componentDidUpdate() {
      // Do this on mount because the tBodyComponent and tBodyGroups change after
      this.deleteListeners()
      this.addListeners()
    }

    addListeners = () => {
      this._tBodyComponent = document.getElementsByClassName('rt-tbody')[0]
      this._tBodyComponent.addEventListener('scroll', this.handleScroll)
    }

    deleteListeners = () => {
      if (this._tBodyComponent != null) {
        this._tBodyComponent.removeEventListener('scroll', this.handleScroll)
      }
    }

    updateWindowDimensions() {
      if (
        this.state.height !== window.innerHeight &&
        this.state.width !== window.innerWidth
      ) {
        this.setState({height: window.innerHeight, width: window.innerWidth})
      } else if (this.state.height !== window.innerHeight) {
        this.setState({height: window.innerHeight})
      } else if (this.state.width !== window.innerWidth) {
        this.setState({width: window.innerWidth})
      }
    }

    // this is so we can expose the underlying ReactTable to get at the sortedData for selectAll
    getWrappedInstance() {
      if (!this.wrappedInstance)
        console.warn('RTSelectTable - No wrapped instance')
      if (this.wrappedInstance.getWrappedInstance)
        return this.wrappedInstance.getWrappedInstance()
      else return this.wrappedInstance
    }

    getColumnsLargestWidth(columns, data) {
      let largestWidth = 0
      columns.forEach((element) => {
        var width = 0
        if (element.accessor != null) {
          width = this.getColumnWidth(data, element.accessor, element.Header)
        }

        if (element.columns != null) {
          let tempWidth = this.getColumnsLargestWidth(element.columns, data)
          if (tempWidth > width) {
            width = tempWidth
          }
        }

        if (width > largestWidth) {
          largestWidth = width
        }
      })

      return largestWidth
    }

    getColumnWidth(data, accessor, headerText) {
      let max = 0

      for (var i = 0; i < data.length; i++) {
        if (data[i] !== undefined && data[i][accessor] !== null) {
          let text = JSON.stringify(data[i][accessor] || 'null')
          let txtSize = textSize(
            text,
            '"Helvetica Neue",Helvetica,Arial,sans-serif',
          ).width
          if (txtSize > max) {
            max = txtSize
          }
        }
      }

      // Make sure we are getting the largest size and including the header
      return Math.max(max, textSize(headerText, '').width)
    }

    updateColumnsWithMinWidth(columns, minWidth) {
      var newColumns = []
      columns.forEach((element) => {
        let newColumn = Object.assign({}, element, {minWidth: minWidth})
        if (element.columns != null) {
          newColumn.columns = this.updateColumnsWithMinWidth(
            element.columns,
            minWidth,
          )
        }
        newColumns.push(newColumn)
      })
      return newColumns
    }

    render() {
      const {
        // CFXProReactTable Props
        // WARNING if you are using the TbodyComponent or TrGroupComponent props they will be override
        // This is to fix a scrolling bug in safari. Need to figure out a way to combine the incoming and the override one.
        // Issue: https://github.com/react-tools/react-table/issues/534
        // Fix: https://codesandbox.io/s/oo9j4z1139
        useAutoWidth, // (true|false) Tell the table to calculate the largest text size in the table and set all the minWidths to that value
        fixedHeaders, // (true|false) Tell the table to fix the height to the max of the page so that headers stay static and scroll
        heightAdjustment = 0, // (number, default 0) Added amount of height adjustment for fixed headers. Only available with fixedHeaders={true}
        footerText, // (string) Text to show at the base of the table
        highlightColumns, // ([string(header_name)])Property to tell which cell values to highlight
        fixColumn, // (string(header_name)) Tell the table to make the column fixed. It can only fix the first column header
        titleText, // (string) Title of the table
        rightButtonCallback, // (func()) action to perform on refresh click
        rightButtonIconName, // (string) The glyphicon object to use
        TbodyComponent,
        TrGroupComponent,
        // ReactTable Props
        data,
        columns,
        styles,
        defaultPageSize = 0,
        ...rest
      } = this.props

      let showFooter = footerText != null
      let showTitle =
        titleText != null ||
        (rightButtonCallback != null && rightButtonIconName != null)

      var newColumns = Object.assign([], columns)
      if (useAutoWidth) {
        let colWidth = this.getColumnsLargestWidth(columns, data)
        newColumns = this.updateColumnsWithMinWidth(columns, colWidth)
      }

      var newStyles = Object.assign({}, styles)
      if (fixedHeaders) {
        const {width} = this.state
        var adjustment = 175
        // This is needed because the menu adjusts due to it's width not fitting.
        // Also need to adjust in styles/common/global.scss

        // WITH LOGIN
        // if (width >= 768 && width <= 935) {
        //   adjustment += 100
        // } else if (width >= 936 && width <= 1200) {
        //   adjustment += 50
        // }
        // WITHOUT LOGIN
        if (width >= 768 && width <= 852) {
          adjustment += 100
        } else if (width >= 853 && width <= 1200) {
          adjustment += 50
        }

        if (showFooter) {
          adjustment += 30
        }

        // IE isn't using the max-height on a production build on the table.
        if (this.isIE() || this.isIOS9OrBelow()) {
          // 34 is height of row without wrapping in cell. Should be ok to hardcode in this app.
          if (defaultPageSize > 0 && data.length < defaultPageSize) {
            newStyles.height = 34 * (defaultPageSize + 1)
          } else {
            newStyles.height =
              data.length < 9
                ? 34 * (data.length + 1)
                : this.state.height - adjustment + heightAdjustment
          }
        } else {
          newStyles.maxHeight =
            this.state.height - adjustment + heightAdjustment
        }
      }

      // Make sure there is a minimum height
      if (newStyles.height < 102) {
        newStyles.height = 102
      }

      const overrideTbodyComponent = (props) => {
        props.children.forEach((child) => {
          for (let i = 0; i < child.length; i++) {
            child[i] = React.cloneElement(child[i], {
              minWidth: props.style.minWidth,
            })
          }
        })

        return <div className="rt-tbody">{props.children}</div>
      }

      const overrideTrGroupComponent = (props) => {
        return (
          <div
            className="rt-tr-group"
            role="rowgroup"
            style={{minWidth: props.minWidth}}
          >
            {props.children}
          </div>
        )
      }

      const overrideGetTdProps = (props) => {
        const {column} = props
        var className = 'cfx-td-center'
        if (
          highlightColumns != null &&
          _.includes(highlightColumns, column.Header)
        ) {
          className += ' cfx-td-feed'
        }

        if (fixColumn != null && fixColumn === column.Header) {
          className += ' cfx-col-fixed'
        }

        return {className: className}
      }

      return (
        <div className="cfx-rt-table">
          {showTitle && (
            <TableTitle
              text={titleText}
              rightButtonCallback={rightButtonCallback}
              rightButtonIconName={rightButtonIconName}
            />
          )}
          <ComponentToWrap
            data={data}
            columns={newColumns}
            style={newStyles}
            defaultPageSize={defaultPageSize}
            getTdProps={overrideGetTdProps}
            TbodyComponent={overrideTbodyComponent}
            TrGroupComponent={overrideTrGroupComponent}
            {...rest}
            ref={(r) => (this.wrappedInstance = r)}
          />
          {showFooter && <TableFooter text={footerText} />}
        </div>
      )
    }
  }
}

const TableTitle = ({text, rightButtonCallback, rightButtonIconName}) => {
  if (
    (rightButtonCallback != null && rightButtonIconName == null) ||
    (rightButtonCallback == null && rightButtonIconName != null)
  ) {
    console.error(
      'To display a right button item you must provide the rightButtonCallback and rightButtonIconName properties.',
    )
  }

  return (
    <div className="cfx-rt-title">
      {text}
      {rightButtonCallback != null &&
        rightButtonIconName && (
          <HeaderButton
            float="right"
            iconName={rightButtonIconName}
            onClick={rightButtonCallback}
          />
        )}
    </div>
  )
}

const HeaderButton = ({float, iconName, onClick}) => {
  return (
    <a
      style={{float: float}}
      className="cfx-rt-header-button"
      onClick={() => onClick()}
    >
      <i className={`fa ${iconName}`} />
    </a>
  )
}

const TableFooter = ({text}) => {
  return <div className="cfx-rt-footer">{text}</div>
}

cfxProReactTable.propTypes = {
  useAutoWidth: PropTypes.bool, // (true|false) Tell the table to calculate the largest text size in the table and set all the minWidths to that value
  fixedHeaders: PropTypes.bool, // (true|false) Tell the table to fix the height to the max of the page so that headers stay static and scroll
  heightAdjustment: PropTypes.number, // (number, default 0) Added amount of height adjustment for fixed headers. Only available with fixedHeaders={true}
  footerText: PropTypes.string, // (string) Text to show at the base of the table
  highlightColumns: PropTypes.arrayOf(PropTypes.string), // ([string(header_name)])Property to tell which cell values to highlight
  fixColumn: PropTypes.string, // (string(header_name)) Tell the table to make the column fixed. It can only fix the first column header
  titleText: PropTypes.string, // (string) Title of the table
  rightButtonCallback: PropTypes.func, // (func()) action to perform on refresh click
  rightButtonIconName: PropTypes.string, // (string) The glyphicon object to use
}

const CFXProReactTable = cfxProReactTable(ReactTable)
export default CFXProReactTable



// WEBPACK FOOTER //
// ./src/components/cfxProReactTable.js